import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { CourseTreeNode, CourseTreeNodeType, DEFAULT_EXPANDED_COURSE_NODE } from '../types';
import { SchoolControllerApiService } from '@lx/shared/api';
import { getLocale } from '@lx/shared/i18n';
import { getCompanyHeader } from '@lx/shared/tokens';
import { switchMap } from 'rxjs/operators';
import { Course } from '@lx/shared/data-access/types';
import { sortArray } from '@lx/shared/utils-array';
import { ProductPackageEnum } from '@lx/quote-tool/shared/types';

/**
 * Service to manage courses for QT Lite.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class CourseService {
  private readonly locale = getLocale();
  private readonly companyHeader = getCompanyHeader();
  private readonly schoolService = inject(SchoolControllerApiService);

  /**
   * Get junior courses
   * @param schoolCode the code of the school to get the courses from
   */
  getJuniorCourses(schoolCode: string): Observable<Course[]> {
    return this.getCoursesByFilter(schoolCode, (course) => this.isJuniorCourse(course));
  }

  /**
   * Get adult courses
   * @param schoolCode the code of the school to get the courses from
   */
  getAdultCourses(schoolCode: string): Observable<Course[]> {
    return this.getCoursesByFilter(schoolCode, (course) => !this.isJuniorCourse(course));
  }

  /**
   * It gets the courses in a tree structure.
   * The parent or root nodes are the course types and the children are the courses.
   *
   * Both roots and children are sorted by weight.
   *
   * @param schoolCode the code of the school to get the courses from
   */
  getCoursesTree(schoolCode: string): Observable<CourseTreeNode[]> {
    if (!schoolCode) {
      return of([]);
    }

    return this.schoolService.findSchoolCourses(schoolCode, this.locale, this.companyHeader).pipe(
      switchMap((courses) => {
        if (!courses || courses.length === 0) {
          return of([]);
        }

        // Build the courses tree
        const coursesTree = this.buildCoursesTree(courses);

        // Sort the parent by weight (the course types)
        const sortedCoursesTree = sortArray(coursesTree, [{ property: 'weight' }, { property: 'label' }]);

        // Sort the children by weight (the courses)
        sortedCoursesTree.forEach((courseType) => {
          const sortedCourses = sortArray(courseType.children, [{ property: 'weight' }]);
          courseType.children = [...sortedCourses];
        });

        // Expand the first node by default
        sortedCoursesTree[DEFAULT_EXPANDED_COURSE_NODE].expanded = true;

        return of(sortedCoursesTree);
      }),
    );
  }

  /**
   * Get courses filtered by a predicate function
   * @param schoolCode the code of the school to get the courses from
   * @param filterFn the predicate function to filter courses
   */
  private getCoursesByFilter(schoolCode: string, filterFn: (course: Course) => boolean): Observable<Course[]> {
    if (!schoolCode) {
      return of([]);
    }
    return this.schoolService
      .findSchoolCourses(schoolCode, this.locale, this.companyHeader)
      .pipe(map((courses: Course[]) => (courses?.length ? courses.filter(filterFn) : [])));
  }

  /**
   * Builds the courses tree from the list of courses.
   */
  private buildCoursesTree(courses: Course[]) {
    return courses.reduce(
      (
        coursesTree: CourseTreeNode[],
        { code, name, weight, type, durationMin, durationMax, lesson: { numberOfLessons, numberOfLessonsDescription }, courseDates, productPackage }: Course,
      ) => {
        let parentNode = coursesTree.find(({ key }) => key === type?.code);
        if (!parentNode) {
          const { code: courseTypeCode, name: courseTypeName, weight: courseTypeWeight } = type;
          parentNode = {
            key: courseTypeCode,
            label: courseTypeName,
            weight: courseTypeWeight,
            children: [],
            type: CourseTreeNodeType.courseType,
          };
          coursesTree.push(parentNode);
        }
        const courseDetails: CourseDetails = { code, name, type: type.code, durationMin, durationMax, courseDates };

        // Build courseDetails object conditionally including productPackage
        if (productPackage) {
          courseDetails.productPackage = productPackage;
        }
        parentNode.children.push({
          key: code,
          label: name,
          subLabel: this.getNumberOfLessonsSubLabel(numberOfLessons, numberOfLessonsDescription),
          weight: weight,
          courseDetails,
          parent: parentNode,
          type: CourseTreeNodeType.course,
        });

        return coursesTree;
      },
      [],
    );
  }

  /**
   * Builds the subLabel for a course based on the number of lessons and its description.
   * @param numberOfLessons the number of lessons per week for the course
   * @param numberOfLessonsDescription the description of the number of lessons (e.g. "varies depending on the week")
   */
  private getNumberOfLessonsSubLabel(numberOfLessons: number, numberOfLessonsDescription: string) {
    if (!numberOfLessons) {
      return $localize`Lessons/week vary`;
    }

    const numberOfLessonsSubLabel = numberOfLessons === 1 ? $localize`1 lesson/week` : $localize`${numberOfLessons} lessons/week`;
    return numberOfLessonsDescription ? `${numberOfLessonsSubLabel} & ${numberOfLessonsDescription}` : numberOfLessonsSubLabel;
  }

  /**
   * Checks if the course is a junior course based on its product package.
   * @param course the course to check
   */
  private isJuniorCourse(course: Course): boolean {
    return course?.productPackage === ProductPackageEnum.YOUNG_LEARNER_CAMPS;
  }
}
